/**
 * Parse the commit identity carried by APP_VERSION.
 *
 * Kubernetes/AWS builds stamp `<semver>+g<short-hex>` while the single-server
 * source rail stamps the exact lower-case 40-hex source release.
 *
 * @param {string | null | undefined} value
 * @returns {string | null}
 */
function commitFromAppVersion(value) {
  const normalized = (value || '').trim();
  const isLowerHex = (candidate, minimum, maximum) =>
    candidate.length >= minimum &&
    candidate.length <= maximum &&
    [...candidate].every((character) => '0123456789abcdef'.includes(character));

  if (isLowerHex(normalized, 40, 40)) return normalized;
  if ((normalized.match(/\+g/g) || []).length === 1) {
    const [version, commit] = normalized.split('+g');
    if (version && isLowerHex(commit, 7, 40)) return commit;
  }
  return null;
}

/**
 * Keep version-regression table headers compact for either deployment rail.
 * Unknown/legacy values remain visible verbatim instead of being guessed.
 *
 * @param {string | null | undefined} value
 * @returns {string}
 */
function formatAppVersionLabel(value) {
  const normalized = (value || '').trim();
  const commit = commitFromAppVersion(normalized);
  if (!commit) return normalized;
  if (commit === normalized) return `source ·${commit.slice(0, 4)}`;
  return `${normalized.slice(0, -(commit.length + 2))} ·${commit.slice(0, 4)}`;
}

/**
 * Preserve the compact `v<semver>` badge for canonical production versions,
 * while rendering a source release as a short, truthful source identity.
 *
 * @param {string | null | undefined} value
 * @returns {string}
 */
function formatAppVersionBadge(value) {
  const normalized = (value || '').trim() || 'dev';
  const commit = commitFromAppVersion(normalized);
  if (commit === normalized) return `source ·${commit.slice(0, 4)}`;
  const version = commit ? normalized.slice(0, -(commit.length + 2)) : normalized.split('+')[0];
  return `v${version.replace(/^v/, '')}`;
}

module.exports = { commitFromAppVersion, formatAppVersionBadge, formatAppVersionLabel };
